import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { styles } from "./style.js";
import { formatDateDDMMYYYY } from "../../../hooks/commonHooks";
import {
  formatSecondsAsClock,
  voiceClockToSeconds,
} from "../../../utils/voiceClock";

const CALL_COLOR = "#6abfa3";
const CHAT_COLOR = "#5c6ce4";
const ACTIONS_COLOR = "#d97706";

function formatMinutesAxis(value) {
  return formatSecondsAsClock(value);
}

function formatActionsAxis(value) {
  return toNumber(value).toLocaleString("en-IN");
}

function toNumber(value) {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
}

function formatChartLabel(point, groupBy) {
  const start = formatDateDDMMYYYY(point.date) || point.date || "";
  if (groupBy === "day" || !point.period_end) return start;
  const end = formatDateDDMMYYYY(point.period_end) || point.period_end;
  return `${start} – ${end}`;
}

function formatTooltipValue(point) {
  if (point.dataKey === "activity_events") {
    return `${toNumber(point.value).toLocaleString("en-IN")} actions`;
  }
  return formatSecondsAsClock(point.value);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={styles.tooltip}>
      <p style={styles.tooltipLabel}>{label}</p>
      {payload.map((point) => (
        <p key={point.dataKey} style={{ ...styles.tooltipRow, color: point.color }}>
          <span style={styles.tooltipDot(point.color)} />
          {point.name}: <strong>{formatTooltipValue(point)}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AgentUseageChart({ data = [], groupBy = "day" }) {
  const chartData = data.map((point) => ({
    label: formatChartLabel(point, groupBy),
    call_minutes: voiceClockToSeconds(point.call_minutes ?? point.minutes),
    chat_minutes: voiceClockToSeconds(point.chat_minutes),
    activity_events: toNumber(point.activity_events),
  }));
  const ranged = groupBy !== "day";

  if (!chartData.length) {
    return <p className="text-muted m-0">No usage recorded for this cycle.</p>;
  }

  return (
    <div className="usage-chart-surface" style={styles.card}>
      <div className="usage-chart-plot" style={styles.chartWrap}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 16, left: 8, bottom: ranged ? 36 : 8 }}
          >
            <defs>
              <linearGradient id="gradCallMinutes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CALL_COLOR} stopOpacity={0.45} />
                <stop offset="95%" stopColor={CALL_COLOR} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="gradChatMinutes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHAT_COLOR} stopOpacity={0.35} />
                <stop offset="95%" stopColor={CHAT_COLOR} stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={styles.axisTick}
              axisLine={false}
              tickLine={false}
              interval={ranged ? 0 : "preserveStartEnd"}
              angle={ranged ? -30 : 0}
              textAnchor={ranged ? "end" : "middle"}
              height={ranged ? 96 : 32}
              dy={ranged ? 4 : 8}
            />
            <YAxis
              yAxisId="minutes"
              tickFormatter={formatMinutesAxis}
              tick={styles.axisTick}
              axisLine={false}
              tickLine={false}
              width={45}
            />
            <YAxis
              yAxisId="actions"
              orientation="right"
              tickFormatter={formatActionsAxis}
              tick={styles.axisTick}
              axisLine={false}
              tickLine={false}
              width={56}
              label={{
                value: "actions",
                angle: -90,
                position: "insideRight",
                style: { fontSize: 11, fill: "#6d748c" },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={styles.legendLabel} />
            <Area
              yAxisId="minutes"
              type="monotone"
              dataKey="call_minutes"
              name="Call minutes"
              stroke={CALL_COLOR}
              strokeWidth={2}
              fill="url(#gradCallMinutes)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: CALL_COLOR }}
            />
            <Area
              yAxisId="minutes"
              type="monotone"
              dataKey="chat_minutes"
              name="Chat minutes"
              stroke={CHAT_COLOR}
              strokeWidth={2}
              fill="url(#gradChatMinutes)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: CHAT_COLOR }}
            />
            <Line
              yAxisId="actions"
              type="monotone"
              dataKey="activity_events"
              name="Actions"
              stroke={ACTIONS_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: ACTIONS_COLOR }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
